/* eslint-disable react-hooks/exhaustive-deps */
/*
  AdminOntologyGovernance — Stage D admin UI for the versioned, governed ontology.

  Four panes:
    1. Live + active archetypes table (with safety badges)
    2. Shadow queue (proposed/shadow archetypes — review & promote)
    3. Snapshots (take / list / rollback)
    4. Match tester (sandbox: see what the engine matches against arbitrary text)
*/
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Layers, Camera, Shuffle, Activity, CheckCircle2, AlertTriangle,
  GitBranch, Sliders,
} from "lucide-react";

const STATUS_STYLE = {
  active:     "bg-sage/30 text-cocoa",
  shadow:     "bg-blush/40 text-mauve",
  proposed:   "bg-champagne/60 text-cocoa",
  deprecated: "bg-cocoa/15 text-cocoa/55",
  archived:   "bg-cocoa/10 text-cocoa/45",
};

const VALID_TRANSITIONS = {
  proposed:   ["shadow", "archived"],
  shadow:     ["active", "archived"],
  active:     ["deprecated", "shadow"],
  deprecated: ["active", "archived"],
  archived:   [],
};

function Bar({ v }) {
  const pct = Math.max(0, Math.min(1, v ?? 0)) * 100;
  return (
    <div className="w-24 h-1.5 bg-champagne/40 rounded-full overflow-hidden inline-block align-middle">
      <div className="h-full bg-mauve" style={{ width: `${pct}%` }} />
    </div>
  );
}

function VersionRow({ a, onChanged }) {
  const [editing, setEditing] = useState(null); // 'status' | 'weights' | null
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [conf, setConf] = useState(a.confidence ?? 0.5);
  const [actW, setActW] = useState(a.activation_weight ?? 0.1);
  const [stab, setStab] = useState(a.stability_score ?? 0.5);
  const [nextStatus, setNextStatus] = useState((VALID_TRANSITIONS[a.status] || [])[0] || "");

  const transitions = VALID_TRANSITIONS[a.status] || [];
  const safetyOk = (a.confidence ?? 0) >= 0.6 && (a.stability_score ?? 0) >= 0.5;

  const saveStatus = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/ontology/v2/${encodeURIComponent(a.archetype_key)}/${a.version}/status`,
        { new_status: nextStatus });
      setEditing(null); onChanged && onChanged();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const saveWeights = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/ontology/v2/${encodeURIComponent(a.archetype_key)}/${a.version}/weights`,
        { confidence: conf, activation_weight: actW, stability_score: stab });
      setEditing(null); onChanged && onChanged();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <tr data-testid={`onto-v2-row-${a.archetype_key}-v${a.version}`} className="border-b border-champagne/40 align-top">
      <td className="py-2 pr-3 text-cocoa font-medium sm:font-normal">{a.name}<div className="text-[10px] text-cocoa/45 uppercase tracking-wider">{a.archetype_key} · v{a.version}</div></td>
      <td data-label="Category" className="py-2 pr-3 text-cocoa/80 text-xs">{a.category}</td>
      <td data-label="Status" className="py-2 pr-3">
        <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[a.status] || STATUS_STYLE.archived}`}>
          {a.status}
        </span>
        {a.status === "active" && (
          safetyOk
            ? <span data-testid={`safety-ok-${a.archetype_key}-v${a.version}`} className="ml-2 text-[10px] text-sage-dark inline-flex items-center"><CheckCircle2 size={11} /> safety-cleared</span>
            : <span className="ml-2 text-[10px] text-mauve inline-flex items-center"><AlertTriangle size={11} /> below safety bar</span>
        )}
      </td>
      <td data-label="Weights" className="py-2 pr-3 text-xs text-cocoa/70">
        <div>conf <Bar v={a.confidence} /> <span className="ml-2">{(a.confidence ?? 0).toFixed(2)}</span></div>
        <div className="mt-0.5">activ <Bar v={a.activation_weight} /> <span className="ml-2">{(a.activation_weight ?? 0).toFixed(2)}</span></div>
        <div className="mt-0.5">stab <Bar v={a.stability_score} /> <span className="ml-2">{(a.stability_score ?? 0).toFixed(2)}</span></div>
      </td>
      <td data-label="Matches" className="py-2 pr-3 text-cocoa/80 text-xs">{a.match_count ?? 0}</td>
      <td data-label="Resonance" className="py-2 pr-3 text-xs text-cocoa/65">
        {(a.resonance_score ?? 0).toFixed(2)}
      </td>
      <td data-label="Govern" className="py-2 pr-3">
        <div className="flex gap-1 flex-wrap">
          {transitions.length > 0 && (
            <button data-testid={`status-${a.archetype_key}-v${a.version}`}
              onClick={() => setEditing(editing === "status" ? null : "status")}
              className="ss-pill ss-pill-ghost h-8 text-[11px]">↪ Status</button>
          )}
          <button data-testid={`weights-${a.archetype_key}-v${a.version}`}
            onClick={() => setEditing(editing === "weights" ? null : "weights")}
            className="ss-pill ss-pill-ghost h-8 text-[11px]"><Sliders size={11} /> Weights</button>
        </div>
        {editing === "status" && transitions.length > 0 && (
          <div className="mt-2 flex gap-1">
            <select value={nextStatus} onChange={(e) => setNextStatus(e.target.value)}
              className="bg-ivory border border-champagne rounded-md h-8 px-2 text-xs">
              {transitions.map((s) => <option key={s} value={s}>→ {s}</option>)}
            </select>
            <button data-testid={`status-${a.archetype_key}-v${a.version}-save`} disabled={busy} onClick={saveStatus}
              className="ss-pill ss-pill-primary h-8 text-[11px]">Apply</button>
          </div>
        )}
        {editing === "weights" && (
          <div className="mt-2 space-y-1">
            {[["confidence", conf, setConf], ["activation", actW, setActW], ["stability", stab, setStab]].map(([k, v, set]) => (
              <div key={k} className="flex items-center gap-2 text-xs">
                <span className="w-14 text-cocoa/55">{k}</span>
                <input data-testid={`weight-${a.archetype_key}-${k}`} type="number" min="0" max="1" step="0.05"
                  value={v} onChange={(e) => set(parseFloat(e.target.value))}
                  className="bg-ivory border border-champagne rounded-md h-7 px-2 w-16" />
              </div>
            ))}
            <button data-testid={`weights-${a.archetype_key}-v${a.version}-save`} disabled={busy} onClick={saveWeights}
              className="ss-pill ss-pill-primary h-8 text-[11px]">Save weights</button>
          </div>
        )}
        {err && <p className="text-rose text-[10px] mt-1">{err}</p>}
      </td>
    </tr>
  );
}

function VersionedTable({ filter }) {
  const [items, setItems] = useState([]);
  const load = async () => { setItems((await api.get("/admin/ontology/v2")).data?.items || []); };
  useEffect(() => { load(); }, []);
  const filtered = items.filter((a) => filter === "all" ? true : a.status === filter);

  return (
    <div className="ss-card p-5 mt-4">
      <table className="w-full text-sm ss-table-stack">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.3em] uppercase text-cocoa/55 border-b border-champagne">
            <th className="py-2 pr-3">Archetype</th>
            <th className="py-2 pr-3">Cat</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Weights</th>
            <th className="py-2 pr-3">Matches</th>
            <th className="py-2 pr-3">Resonance</th>
            <th className="py-2 pr-3">Govern</th>
          </tr>
        </thead>
        <tbody>
          {filtered.slice(0, 60).map((a) => (
            <VersionRow key={`${a.archetype_key}-v${a.version}`} a={a} onChanged={load} />
          ))}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <p className="py-6 text-cocoa/50 text-sm text-center">No archetypes match this filter.</p>
      )}
    </div>
  );
}

function SnapshotsPane() {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmFor, setConfirmFor] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = async () => { setItems((await api.get("/admin/ontology/v2/snapshots/list")).data?.items || []); };
  useEffect(() => { load(); }, []);

  const take = async () => {
    if (!label || label.length < 2) return;
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/admin/ontology/v2/snapshots", { label, notes: notes || undefined });
      setMsg(`Snapshot taken: ${r.data.id}`); setLabel(""); setNotes(""); await load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const rollback = async (id) => {
    setBusy(true); setMsg("");
    try {
      const r = await api.post(`/admin/ontology/v2/snapshots/${id}/rollback`, { confirm_label: confirmText });
      setMsg(`Rolled back: restored=${r.data.restored} archived=${r.data.archived}`);
      setConfirmFor(null); setConfirmText("");
    } catch (e) { setMsg(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="onto-v2-snapshots" className="ss-card p-6 mt-6">
      <div className="flex items-center gap-2">
        <Camera size={14} className="text-mauve" />
        <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Snapshots</p>
      </div>
      <h2 className="font-heading text-2xl text-cocoa mt-1">Whole-ontology versioning.</h2>
      <p className="text-cocoa/65 text-sm mt-1">
        Each snapshot freezes the current status/weights of every archetype so you can roll back
        if a new generation drifts. Rollback archives any archetype created after the snapshot,
        preserving lineage history.
      </p>
      <div className="mt-4 grid sm:grid-cols-3 gap-2">
        <input data-testid="snapshot-label" value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder="Snapshot label (e.g. 'pre-2026-launch')"
          className="bg-ivory border border-champagne rounded-md h-10 px-3 text-sm" />
        <input data-testid="snapshot-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes"
          className="bg-ivory border border-champagne rounded-md h-10 px-3 text-sm sm:col-span-2" />
      </div>
      <div className="mt-2 flex items-center gap-3">
        <button data-testid="snapshot-take" disabled={busy || label.length < 2} onClick={take}
          className="ss-pill ss-pill-primary h-10 px-5 disabled:opacity-50"><Camera size={13} /> Take snapshot</button>
        {msg && <span className="text-cocoa/70 text-xs">{msg}</span>}
      </div>

      <div className="mt-5 space-y-2">
        {items.map((s) => (
          <div key={s.id} data-testid={`snapshot-${s.id}`} className="ss-card p-4 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="font-heading text-lg text-cocoa">{s.label}</p>
              <p className="text-[10px] tracking-widest uppercase text-cocoa/55">{s.archetypes_count} archetypes · {s.taken_at}</p>
              {s.notes && <p className="text-cocoa/65 text-sm mt-1">{s.notes}</p>}
            </div>
            <div className="flex flex-col items-end gap-2">
              {confirmFor === s.id ? (
                <div className="flex items-center gap-2">
                  <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type label to confirm"
                    className="bg-ivory border border-champagne rounded-md h-8 px-2 text-xs w-44" />
                  <button data-testid={`snapshot-${s.id}-rollback-confirm`} disabled={busy || confirmText !== s.label}
                    onClick={() => rollback(s.id)}
                    className="ss-pill ss-pill-primary h-8 text-[11px] disabled:opacity-50">Confirm rollback</button>
                  <button onClick={() => { setConfirmFor(null); setConfirmText(""); }}
                    className="ss-pill ss-pill-ghost h-8 text-[11px]">Cancel</button>
                </div>
              ) : (
                <button data-testid={`snapshot-${s.id}-rollback`} onClick={() => setConfirmFor(s.id)}
                  className="ss-pill ss-pill-ghost h-8 text-[11px]"><Shuffle size={11} /> Rollback to here</button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-cocoa/50 text-sm">No snapshots yet.</p>}
      </div>
    </section>
  );
}

function MatchTester() {
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("live");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const run = async () => {
    setBusy(true); setErr(""); setRes(null);
    try {
      const r = await api.post("/admin/ontology/v2/match-test", { query_text: query, mode, k: 8 });
      setRes(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="onto-v2-match" className="ss-card p-6 mt-6">
      <div className="flex items-center gap-2">
        <Activity size={14} className="text-mauve" />
        <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Match tester</p>
      </div>
      <h2 className="font-heading text-2xl text-cocoa mt-1">Sandbox the matcher.</h2>
      <p className="text-cocoa/65 text-sm mt-1">
        Type a sample situation and see what the ontology engine matches. Switch to
        <strong> shadow</strong> mode to include shadow-only archetypes in the candidate set
        without affecting live user sessions.
      </p>
      <textarea data-testid="match-query" value={query} onChange={(e) => setQuery(e.target.value)}
        rows={3} placeholder="e.g. He cancels often citing work but still says he wants to meet my family."
        className="mt-3 w-full bg-ivory border border-champagne rounded-md p-3 text-sm" />
      <div className="mt-2 flex items-center gap-2">
        <select data-testid="match-mode" value={mode} onChange={(e) => setMode(e.target.value)}
          className="bg-ivory border border-champagne rounded-md h-10 px-3 text-sm">
          <option value="live">Live mode</option>
          <option value="shadow">Shadow mode</option>
        </select>
        <button data-testid="match-run" disabled={busy || query.length < 4} onClick={run}
          className="ss-pill ss-pill-primary h-10 px-5 disabled:opacity-50">Run match</button>
        {err && <span className="text-rose text-xs">{err}</span>}
      </div>
      {res && (
        <div data-testid="match-results" className="mt-4 space-y-2">
          {(res.matches || []).map((m, i) => (
            <div key={i} className="ss-card p-3 flex items-center justify-between">
              <div>
                <p className="text-cocoa">{m.name} <span className="text-cocoa/45 text-xs">v{m.archetype_version}</span></p>
                <p className="text-[10px] tracking-widest uppercase text-cocoa/55">
                  raw {m.raw_similarity} · weighted {m.weighted_score}
                </p>
              </div>
              <div className="text-[10px] text-cocoa/55 flex gap-2">
                <span>c {(m.confidence ?? 0).toFixed(2)}</span>
                <span>a {(m.activation_weight ?? 0).toFixed(2)}</span>
                <span>s {(m.stability_score ?? 0).toFixed(2)}</span>
              </div>
            </div>
          ))}
          {(res.matches || []).length === 0 && (
            <p className="text-cocoa/50 text-sm">{res.reason === "no_embedding_key" ? "OpenAI API key not configured — matcher returns empty." : "No matches in candidate set."}</p>
          )}
        </div>
      )}
    </section>
  );
}

function VectorStatsCard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/vector/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return null;
  return (
    <div data-testid="onto-v2-vector-stats" className="ss-card p-5 mt-4 grid sm:grid-cols-3 gap-4">
      <div>
        <p className="text-[10px] tracking-[0.35em] uppercase text-cocoa/55">Embeddings stored</p>
        <p className="font-heading text-3xl text-cocoa">{stats.embeddings_total}</p>
        <p className="text-xs text-cocoa/55">across {stats.embeddings_by_source.length} source types</p>
      </div>
      <div>
        <p className="text-[10px] tracking-[0.35em] uppercase text-cocoa/55">Archetypes (versioned)</p>
        <p className="font-heading text-3xl text-cocoa">{stats.archetypes_total}</p>
        <div className="mt-1 flex gap-2 text-[10px] tracking-widest uppercase text-cocoa/55">
          {stats.archetypes_by_status.map((s) => <span key={s.status}>{s.status}: {s.n}</span>)}
        </div>
      </div>
      <div>
        <p className="text-[10px] tracking-[0.35em] uppercase text-cocoa/55">By source</p>
        <ul className="mt-1 text-xs text-cocoa/70 space-y-0.5">
          {stats.embeddings_by_source.length === 0 && <li className="text-cocoa/40">No embeddings yet — OPENAI_API_KEY not configured.</li>}
          {stats.embeddings_by_source.map((s, i) => (
            <li key={i}>{s.source_type} <span className="text-cocoa/45">({s.anonymisation_status})</span>: {s.n}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function AdminOntologyGovernance() {
  const [filter, setFilter] = useState("active");
  return (
    <div data-testid="admin-ontology-governance">
      <section className="ss-card p-6">
        <div className="flex items-center gap-2">
          <GitBranch size={14} className="text-mauve" />
          <p className="text-[11px] tracking-[0.35em] uppercase text-mauve">Versioned ontology</p>
        </div>
        <h2 className="font-heading text-2xl text-cocoa mt-1">Governance & lineage.</h2>
        <p className="text-cocoa/65 text-sm mt-1">
          Every archetype has a version, confidence, activation weight and stability score. Live advisor
          inference only uses archetypes with status <code>active</code>, confidence ≥ 0.60 AND stability ≥ 0.50.
          Below that bar, archetypes still influence reads but never safety-sensitive guidance.
        </p>

        <VectorStatsCard />

        <div className="mt-5 flex gap-1 flex-wrap">
          {["active", "shadow", "proposed", "deprecated", "archived", "all"].map((s) => (
            <button key={s} data-testid={`onto-v2-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-3 h-9 text-xs rounded-full ${filter === s ? "bg-cocoa text-ivory" : "bg-champagne/40 text-cocoa/70"}`}>
              {s}
            </button>
          ))}
        </div>
        <VersionedTable filter={filter} />
      </section>

      <MatchTester />
      <SnapshotsPane />
    </div>
  );
}
